import { Axiom } from '@axiomhq/js';
import { onCLS, onINP, onFCP, onLCP, onTTFB, type Metric } from 'web-vitals';

let axiomInstance: Axiom | null = null;

interface AxiomConfig {
  token: string;
  dataset: string;
  orgId?: string;
}

export function initAxiom() {
  // Só inicializa se estiver configurado
  const token = import.meta.env.VITE_AXIOM_TOKEN;
  const dataset = import.meta.env.VITE_AXIOM_DATASET;
  const orgId = import.meta.env.VITE_AXIOM_ORG_ID;

  if (!token || !dataset) {
    console.log('Axiom não configurado - métricas não serão enviadas');
    return null;
  }

  if (!axiomInstance) {
    const config: AxiomConfig = { token, dataset };
    if (orgId) {
      config.orgId = orgId;
    }

    axiomInstance = new Axiom(config);

    // Capturar Web Vitals automaticamente
    captureWebVitals();

    // Capturar erros não tratados
    window.addEventListener('error', (event) => {
      logError(event.error || new Error(event.message), {
        type: 'unhandled_error',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    });

    // Capturar promises rejeitadas não tratadas
    window.addEventListener('unhandledrejection', (event) => {
      logError(event.reason instanceof Error ? event.reason : new Error(String(event.reason)), {
        type: 'unhandled_rejection',
      });
    });
  }

  return axiomInstance;
}

// Captura automática de Web Vitals
function captureWebVitals() {
  const sendToAxiom = (metric: Metric) => {
    sendMetric(`web_vitals.${metric.name.toLowerCase()}`, metric.value, {
      rating: metric.rating || 'unknown',
      navigationType: metric.navigationType || 'unknown',
    });
  };

  onCLS(sendToAxiom);
  onINP(sendToAxiom); // INP substituiu FID no web-vitals v3+
  onFCP(sendToAxiom);
  onLCP(sendToAxiom);
  onTTFB(sendToAxiom);
}

// Helper para enviar logs
export async function logToAxiom(
  level: 'info' | 'warn' | 'error' | 'debug',
  message: string,
  data?: Record<string, unknown>
) {
  const axiom = axiomInstance || initAxiom();
  if (!axiom) return;

  const dataset = import.meta.env.VITE_AXIOM_DATASET || 'nexops-dashboard';

  try {
    await axiom.ingest(dataset, [
      {
        timestamp: new Date().toISOString(),
        level,
        message,
        service: 'dashboard',
        environment: import.meta.env.MODE,
        url: window.location.href,
        userAgent: navigator.userAgent,
        ...data,
      },
    ]);
  } catch (error) {
    console.error('Erro ao enviar log para Axiom:', error);
  }
}

// Helper para métricas customizadas
export async function sendMetric(metric: string, value: number, tags?: Record<string, string>) {
  const axiom = axiomInstance || initAxiom();
  if (!axiom) return;

  const dataset = import.meta.env.VITE_AXIOM_DATASET || 'nexops-dashboard';

  try {
    await axiom.ingest(dataset, [
      {
        timestamp: new Date().toISOString(),
        type: 'metric',
        metric,
        value,
        service: 'dashboard',
        environment: import.meta.env.MODE,
        url: window.location.href,
        ...tags,
      },
    ]);
  } catch (error) {
    console.error('Erro ao enviar métrica para Axiom:', error);
  }
}

// Helper para user actions/events
export async function trackEvent(event: string, properties?: Record<string, unknown>) {
  const axiom = axiomInstance || initAxiom();
  if (!axiom) return;

  const dataset = import.meta.env.VITE_AXIOM_DATASET || 'nexops-dashboard';

  try {
    await axiom.ingest(dataset, [
      {
        timestamp: new Date().toISOString(),
        type: 'event',
        event,
        service: 'dashboard',
        environment: import.meta.env.MODE,
        url: window.location.href,
        ...properties,
      },
    ]);
  } catch (error) {
    console.error('Erro ao enviar evento para Axiom:', error);
  }
}

// Helper para performance timing
export async function trackPerformance(
  operation: string,
  duration: number,
  metadata?: Record<string, unknown>
) {
  const axiom = axiomInstance || initAxiom();
  if (!axiom) return;

  const dataset = import.meta.env.VITE_AXIOM_DATASET || 'nexops-dashboard';

  try {
    await axiom.ingest(dataset, [
      {
        timestamp: new Date().toISOString(),
        type: 'performance',
        operation,
        duration,
        service: 'dashboard',
        environment: import.meta.env.MODE,
        url: window.location.href,
        ...metadata,
      },
    ]);
  } catch (error) {
    console.error('Erro ao enviar performance para Axiom:', error);
  }
}

// Helper para log de erros
export async function logError(error: Error, context?: Record<string, unknown>) {
  const axiom = axiomInstance || initAxiom();
  if (!axiom) return;

  const dataset = import.meta.env.VITE_AXIOM_DATASET || 'nexops-dashboard';

  try {
    await axiom.ingest(dataset, [
      {
        timestamp: new Date().toISOString(),
        type: 'error',
        level: 'error',
        message: error.message,
        stack: error.stack,
        name: error.name,
        service: 'dashboard',
        environment: import.meta.env.MODE,
        url: window.location.href,
        userAgent: navigator.userAgent,
        ...context,
      },
    ]);
  } catch (axiomError) {
    console.error('Erro ao enviar erro para Axiom:', axiomError);
  }
}

// Helper para setar user context
export async function setUserContext(user: {
  id: string;
  email: string;
  name: string;
  role?: string;
}) {
  await logToAxiom('info', 'User context set', {
    type: 'user_context',
    userId: user.id,
    userEmail: user.email,
    userName: user.name,
    userRole: user.role,
  });
}

// Helper para limpar user context
export async function clearUserContext() {
  await logToAxiom('info', 'User context cleared', {
    type: 'user_context_cleared',
  });
}

// Helper para flush de logs pendentes
export async function flushAxiomLogs() {
  if (!axiomInstance) return;

  try {
    await axiomInstance.flush();
  } catch (error) {
    console.error('Erro ao fazer flush dos logs do Axiom:', error);
  }
}

// Hook personalizado para React
export function useAxiomTracking() {
  return {
    trackEvent,
    trackPerformance,
    logError,
    sendMetric,
    setUserContext,
    clearUserContext,
  };
}

export { axiomInstance };
